import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Divider, Typography, IconButton, Grid, Badge, Button } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
	OrderInfo,
	Shopper,
	ResponsiblePersonDetails,
	Discount,
	OrderProduct,
	Location,
	PartialWithId,
	OnlineOrderRequest,
} from 'common/types';
import Drawer from '@mui/material/Drawer';
import Hidden from '@mui/material/Hidden';
import PaymentSection from './PaymentSection';
import ResponsiblePersonInformation from './ResponsiblePersonInformation';
import ChangeRentalStateContainer from './ChangeRentalStateContainer';
import SkidataInformation from './SkidataInformation';
import { closeMobileRentalSideBar, setStartPrint, updateActivePartialOrder } from 'actions';
import { getResponsiblePersonDetails } from 'services/utils';
import PreparationCheckbox from 'components/PreparationCheckbox';
import moment from 'moment-timezone';
import TimeAndPlaceBar from '../TimeAndPlaceBar';
import StartAndEndLocation from '../StartAndEndLocation';
import EditResponsiblePersonDialog from 'components/EditResponsiblePersonDialog';
import { RiPrinterLine, RiMailLine, RiPercentLine, RiChat3Line, RiTruckLine } from 'react-icons/ri';
import DiscountDialog from 'components/DiscountDialog';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { useTranslation } from 'services/localization/useTranslation';
import { usePermissions } from 'hooks/usePermissions';
import useShopFormat from 'hooks/useShopFormat';
import SendEmailDialog from 'components/SendEmailDialog';
import ActionConfirmFeedback from '../ActionConfirmFeedback';
import { getOnlineOrderRequest, isBookedOrderMissingDetails } from 'common/utils/confirmation';
import errorHandler from 'common/services/errorHandler';
import { emailOrderConfirmation } from 'common/api/db/functions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import { replaceValues } from 'common/utils/common';
import { hasLiftTicketProducts, hasOnlyLiftTicketProducts } from 'common/utils/liftTicketUtils';
import LateReturnBanner from './LateReturnBanner';
import { isTimeBeforeTimeNow } from 'services/utils/dateCalculations';
import { getExtendedOrderPricing } from 'common/modules/atoms/pricing';
import { isEqual } from 'lodash';
import { addDiscountToProducts } from 'common/modules/orders/products';
import { getOrderPricing, getPricingOptions } from 'common/modules/orders';
import DeliveryTimeDetails from './DeliveryTimeDetails';
import { getClientEnv } from 'common/utils/frontUtils';
import { getRentalDeliveryEndTime } from 'common/utils/dateCalculations';
import BookingCommentModal from 'components/BookingCommentModal';
import OrderSummaryDialog from './OrderSummary';
import DeliveryDetailsDialog from './DeliveryDetails/DeliveryDetailsDialog';
import { getChannelName } from './utils';

export const SideBar = () => {
	const classes = useStyles();
	const { hasPermission } = usePermissions();

	const rental = useSelector(RentalSelectors.activeOrder)!;
	const products = useSelector(RentalSelectors.orderProducts);
	const shoppers = useSelector(RentalSelectors.orderShoppers);
	const orderDelivery = useSelector(RentalSelectors.orderDelivery);
	const rentalInfo = useSelector(RentalSelectors.orderInfo)!;
	const orderHasRentals = useSelector(RentalSelectors.orderHasRentals);
	const { activeState, rentalState, prepared, services, pricing } = rentalInfo;
	const { t } = useTranslation();
	const { localFormat } = useShopFormat();
	const orderPricing = getExtendedOrderPricing(pricing);

	const dispatch = useDispatch();
	const activeShop = useSelector(ShopSelectors.activeShopData)!;
	const shopLocationsCount = useSelector(ShopSelectors.allShopLocationsCount);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);

	const lang = useSelector(LanguageSelectors.language);
	const categories = useSelector(StockSelectors.categoriesData);
	const mobileRentalSideBarOpen = useSelector(ViewSelectors.mobileRentalSideBarOpen);

	const [editingResponsiblePerson, setEditingResponsiblePerson] = useState(false);
	const [editingAdditionalInformation, setEditingAdditionalInformation] = useState(false);
	const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
	const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
	const [sendEmailLoading, setSendEmailLoading] = useState(false);
	const [sendEmailError, setSendEmailError] = useState(false);
	const [sendEmailActionConfirmed, setSendEmailActionConfirmed] = useState(false);
	const [orderOverviewDialogOpen, setOrderOverviewDialogOpen] = useState<boolean>(false);
	const [deliveryDialogOpen, setDeliveryDialogOpen] = useState<boolean>(false);

	const responsiblePerson = getResponsiblePersonDetails(rentalInfo.responsiblePerson, shoppers);
	const currency = getCurrencyObjectFromCurrencyCode(pricing.currency, shopCurrency);

	const hasTicketProducts = hasLiftTicketProducts(products);
	const hasOnlyTicketProducts = hasOnlyLiftTicketProducts(products);

	const hasDeliveryTo = rentalState === 'BOOKED' && !!services?.delivery?.to && !!orderDelivery?.to;
	const hasDeliveryFrom =
		rentalState === 'ACTIVE' && !!services?.delivery?.from && !!orderDelivery?.from;

	const hasSomeDelivery = hasDeliveryTo || hasDeliveryFrom;

	const rentalDeliveryEndTime = getRentalDeliveryEndTime(rentalInfo);

	const bookingHasComment =
		!!rentalInfo.additionalInformation || !!rentalInfo?.bookingComment?.value;

	const isDeliveryTimeAlreadyPassed = hasDeliveryFrom && isTimeBeforeTimeNow(rentalDeliveryEndTime);

	const orderIsLate =
		isDeliveryTimeAlreadyPassed ||
		(rentalInfo.rentalState === 'ACTIVE' &&
			!!rentalInfo.returnTimeNext &&
			!moment(rentalInfo.startDate).isSame(moment(rentalInfo.returnTimeNext)) &&
			isTimeBeforeTimeNow(rentalInfo.returnTimeNext) &&
			orderHasRentals);

	const handleAdditionalInformationFormChange = async (value: string) => {
		const updatedRentalInfo: Partial<OrderInfo> = {
			additionalInformation: value,
		};
		handleCloseAdditionalInformationDialog();
		dispatch(
			updateActivePartialOrder({
				rentalInfo: {
					id: rentalInfo.id,
					data: updatedRentalInfo,
				},
			}),
		);
	};

	const handleSideBarClose = () => {
		dispatch(closeMobileRentalSideBar());
	};

	const handlePreparedChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		const updatedRentalInfo: Partial<OrderInfo> = {
			prepared: checked,
		};
		dispatch(
			updateActivePartialOrder({
				rentalInfo: {
					id: rentalInfo.id,
					data: updatedRentalInfo,
				},
			}),
		);
	};

	const startEditingResponsiblePerson = () => {
		setEditingResponsiblePerson(true);
	};

	const handleCloseResponsiblePersonDialog = () => {
		setEditingResponsiblePerson(false);
	};

	const startEditingAdditionalInformation = () => {
		setEditingAdditionalInformation(true);
	};

	const handleCloseAdditionalInformationDialog = () => {
		setEditingAdditionalInformation(false);
	};

	const handleOpenSendEmailDialog = () => {
		setSendEmailDialogOpen(true);
	};

	const handleCloseSendEmailDialog = () => {
		setSendEmailDialogOpen(false);
	};

	const handleConfirmFeedbackClose = () => {
		setSendEmailActionConfirmed(false);
	};

	const handleSendBookingConfirmationEmail = async (email: string) => {
		setSendEmailLoading(true);
		if (sendEmailError) {
			setSendEmailError(false);
		}
		try {
			if (!activeShop.shopUrls) {
				throw new Error(`No shop url found for shop ${activeShop.shopInfo.id}`);
			}
			const isMissingDetails = isBookedOrderMissingDetails(products, shoppers);
			const orderData = getOnlineOrderRequest({
				order: rental,
				shopInfo: activeShop.publicInfo,
				categories,
				lang,
				shopUrlDoc: activeShop.shopUrls,
				isMissingDetails,
				locationId: rentalInfo.startLocation.id,
				localFormat,
				t,
				env: getClientEnv(),
			});
			const orderDataNotNaN = replaceValues(orderData, Number.isNaN, 0) as OnlineOrderRequest;
			await emailOrderConfirmation({ data: orderDataNotNaN, email });
			setSendEmailDialogOpen(false);
		} catch (e) {
			errorHandler.report(e);
			setSendEmailError(true);
		}
		setSendEmailLoading(false);
		setSendEmailActionConfirmed(true);
	};

	const handleResponsiblePersonInfoUpdate = async (responsiblePerson: ResponsiblePersonDetails) => {
		if (!rentalInfo.responsiblePerson.external) {
			const shopperId = rentalInfo.responsiblePerson.shopperId;
			const updatedResponsiblePerson: Partial<Shopper> = {
				phone: responsiblePerson.phone,
				email: responsiblePerson.email,
				...(responsiblePerson.homeAddress && { homeAddress: responsiblePerson.homeAddress }),
				...(responsiblePerson.accommodation && { accommodation: responsiblePerson.accommodation }),
			};
			dispatch(
				updateActivePartialOrder({
					shoppers: [
						{
							id: shopperId,
							data: updatedResponsiblePerson,
						},
					],
				}),
			);
		} else {
			const updatedRentalInfo: Partial<OrderInfo> = {
				responsiblePerson: {
					...rentalInfo.responsiblePerson,
					person: {
						...rentalInfo.responsiblePerson.person,
						...responsiblePerson,
					},
				},
			};
			dispatch(
				updateActivePartialOrder({
					rentalInfo: {
						id: rentalInfo.id,
						data: updatedRentalInfo,
					},
				}),
			);
		}
		handleCloseResponsiblePersonDialog();
	};

	const handleLocationUpdate = (location: Location) => {
		const updatedRentalInfo: Partial<OrderInfo> = {
			startLocation: location,
			endLocation: location,
		};

		const updatedProducts: PartialWithId<OrderProduct>[] = products.map((product) => {
			const updatedProduct: Partial<OrderProduct> = {
				startLocationId: location.id,
				endLocationId: location.id,
			};
			const returnProduct: PartialWithId<OrderProduct> = {
				id: product.id,
				data: updatedProduct,
			};
			return returnProduct;
		});

		dispatch(
			updateActivePartialOrder({
				rentalInfo: {
					id: rentalInfo.id,
					data: updatedRentalInfo,
				},
				products: updatedProducts,
			}),
		);
	};

	const startPrint = () => {
		dispatch(setStartPrint(true));
	};

	const openDiscountDialog = () => {
		setDiscountDialogOpen(true);
	};

	const handleDiscountDialogClose = async (discount: Discount) => {
		setDiscountDialogOpen(false);

		if (!isEqual(orderPricing.manualDiscount, discount)) {
			const updatedProducts = addDiscountToProducts(products, discount.amount || 0);
			const newOrderPricing = getOrderPricing(
				updatedProducts.full,
				orderDelivery,
				getPricingOptions(rentalInfo.pricing),
			);
			const newRentalInfo: Partial<OrderInfo> = {
				pricing: newOrderPricing,
			};
			dispatch(
				updateActivePartialOrder({
					rentalInfo: {
						id: rentalInfo.id,
						data: newRentalInfo,
					},
					products: updatedProducts.changes,
				}),
			);
		}
	};

	const renderDeliveryDetails = () => (
		<Grid item xs={6}>
			<DeliveryTimeDetails
				label={t('individualRental.latestAtTheStore', 'Latest at the store')}
				date={rentalDeliveryEndTime}
			/>
		</Grid>
	);

	const drawer = (
		<div className={classes.sidebarContentContainer}>
			<div className={classes.contentWrapper}>
				<div className={classes.actionPanel}>
					<Grid container spacing={1}>
						<Grid item>
							<IconButton aria-label="print" onClick={startPrint} edge="start" size="small">
								<RiPrinterLine />
							</IconButton>
						</Grid>
						<Grid item>
							<IconButton aria-label="email" onClick={handleOpenSendEmailDialog} size="small">
								<RiMailLine />
							</IconButton>
						</Grid>
						<Grid item>
							<IconButton
								aria-label="discount"
								onClick={openDiscountDialog}
								size="small"
								disabled={!hasPermission('edit_booking')}
							>
								<Badge
									invisible={!orderPricing.manualDiscount?.amount}
									variant="dot"
									overlap="circular"
									color="primary"
									classes={{
										badge: classes.discountBadge,
										anchorOriginTopRightCircular: classes.dotBadgeStyles,
									}}
								>
									<RiPercentLine />
								</Badge>
							</IconButton>
						</Grid>
						<Grid item>
							<IconButton
								aria-label="edit-responsible-person"
								onClick={startEditingAdditionalInformation}
								size="small"
							>
								<Badge
									invisible={!bookingHasComment}
									variant="dot"
									overlap="circular"
									color="primary"
									classes={{
										badge: classes.discountBadge,
										anchorOriginTopRightCircular: classes.dotBadgeStyles,
									}}
								>
									<RiChat3Line />
								</Badge>
							</IconButton>
						</Grid>
						{!!services?.delivery && !!orderDelivery && (
							<Grid item>
								<IconButton
									size="small"
									onClick={() => setDeliveryDialogOpen(true)}
									aria-label="shipping-information"
									disabled={!hasPermission('edit_booking')}
								>
									<RiTruckLine />
								</IconButton>
							</Grid>
						)}
					</Grid>
				</div>
				{rentalInfo.additionalInformation && (
					<div>
						<Typography variant="body2">{rentalInfo.additionalInformation}</Typography>
					</div>
				)}
				<Divider className={classes.divider} />
				<Grid container className={classes.bookingInfo}>
					<Grid item xs={6} className={classes.channelGrid}>
						<Typography variant="caption" color="textSecondary">
							{t('orderCreated2', 'Order created')}
						</Typography>
						<Typography variant="caption" color="textSecondary">
							{localFormat(moment(rentalInfo.created), 'DD.MM.YYYY, HH:mm')}
						</Typography>
					</Grid>
					<Grid item xs={6} className={classNames(classes.channelGrid, classes.right)}>
						<Typography variant="caption" color="textSecondary">
							{t('channel', 'Channel')}
						</Typography>
						<Typography variant="caption" color="textSecondary">
							{getChannelName(rentalInfo.channel, t)}
						</Typography>
					</Grid>
				</Grid>
				{responsiblePerson ? (
					<>
						<ResponsiblePersonInformation
							responsiblePerson={responsiblePerson}
							rental={rental}
							startEditingResponsiblePerson={startEditingResponsiblePerson}
						/>
					</>
				) : null}
				<Divider className={classes.divider} />
				{hasTicketProducts && (
					<>
						<SkidataInformation rental={rental} />
						<Divider className={classes.divider} />
					</>
				)}
				<Grid container spacing={2}>
					{hasDeliveryTo && !!services?.delivery?.to ? (
						<Grid item xs={6}>
							<DeliveryTimeDetails
								label={t('rentalCard.mustBePreparedBy')}
								date={services.delivery.to.startDate}
							/>
						</Grid>
					) : (
						hasDeliveryFrom && orderDelivery?.from && renderDeliveryDetails()
					)}

					<Grid item xs={hasSomeDelivery ? 6 : 12}>
						{orderHasRentals && <TimeAndPlaceBar />}
					</Grid>
				</Grid>
				{orderIsLate && <LateReturnBanner mt={1} mb={2} />}
				{!hasDeliveryTo && !hasDeliveryFrom && shopLocationsCount > 1 && (
					<StartAndEndLocation
						location={rentalInfo.startLocation}
						onChange={(location) => handleLocationUpdate(location)}
						rentalState={rentalState}
						hasDelivery={hasSomeDelivery}
					/>
				)}
				<Button
					fullWidth
					variant="text"
					color="secondary"
					size="small"
					onClick={() => setOrderOverviewDialogOpen(true)}
				>
					{t('orderSummaryDialog.cta', 'Order summary')}
				</Button>
				<Divider className={classes.divider} />
				<PaymentSection rental={rental} />
				<Divider className={classes.divider} />
				{rentalState === 'BOOKED' && !hasOnlyTicketProducts ? (
					<PreparationCheckbox checked={Boolean(prepared)} onChange={handlePreparedChange} />
				) : null}
			</div>
			{!hasOnlyTicketProducts && (
				<div className={classes.actionBtnWrapper}>
					{rentalState === 'COMPLETED' && rentalInfo.endDateReturned && orderHasRentals ? (
						<Typography variant="body2" color="textSecondary" gutterBottom>
							{activeState === 'RENT_CANCEL'
								? t('rentalCancelled', 'Rental cancelled')
								: t('rentalEnded', 'Rental ended')}
							{': '}
							{localFormat(moment(rentalInfo.endDateReturned), 'DD.MM.YYYY, HH:mm')}
						</Typography>
					) : null}
					{activeState !== 'RENT_CANCEL' ? <ChangeRentalStateContainer rental={rental} /> : null}
				</div>
			)}
		</div>
	);

	return (
		<div className={classes.drawer}>
			<Hidden mdUp implementation="css">
				<Drawer
					variant="temporary"
					anchor={'right'}
					open={mobileRentalSideBarOpen}
					onClose={handleSideBarClose}
					classes={{ paper: classes.drawerPaper }}
				>
					{drawer}
				</Drawer>
			</Hidden>
			<Hidden mdDown implementation="css">
				<Drawer classes={{ paper: classes.drawerPaper }} variant="permanent" anchor={'right'} open>
					{drawer}
				</Drawer>
			</Hidden>
			{editingResponsiblePerson && responsiblePerson && (
				<EditResponsiblePersonDialog
					open={editingResponsiblePerson}
					handleClose={handleCloseResponsiblePersonDialog}
					handleUpdate={handleResponsiblePersonInfoUpdate}
					responsiblePerson={responsiblePerson}
					shopId={activeShop.shopInfo.id}
				/>
			)}
			{editingAdditionalInformation && (
				<BookingCommentModal
					open={editingAdditionalInformation}
					onCancel={handleCloseAdditionalInformationDialog}
					onEdit={(value) => handleAdditionalInformationFormChange(value)}
					value={rentalInfo.additionalInformation}
					label={t('common:form.comments')}
				/>
			)}
			{discountDialogOpen && (
				<DiscountDialog
					open={discountDialogOpen}
					onClose={handleDiscountDialogClose}
					discount={rentalInfo.pricing.manualDiscount ?? { amount: 0 }}
					originalPrice={orderPricing.subtotalWithoutManualDiscount}
					currency={currency}
				/>
			)}
			{sendEmailDialogOpen && responsiblePerson && (
				<SendEmailDialog
					open={sendEmailDialogOpen}
					email={responsiblePerson.email}
					isSending={sendEmailLoading}
					onClose={handleCloseSendEmailDialog}
					onSubmit={handleSendBookingConfirmationEmail}
				/>
			)}
			{orderOverviewDialogOpen && (
				<OrderSummaryDialog
					open={orderOverviewDialogOpen}
					onClose={() => setOrderOverviewDialogOpen(false)}
				/>
			)}
			{deliveryDialogOpen && !!services?.delivery && !!orderDelivery && (
				<DeliveryDetailsDialog
					open={deliveryDialogOpen}
					onClose={() => setDeliveryDialogOpen(false)}
					orderDeliveryService={orderDelivery}
					orderInfoDelivery={services?.delivery}
				/>
			)}
			<ActionConfirmFeedback
				message={
					sendEmailError
						? t('sendEmailError', 'Send email failed. Please try again.')
						: t('sendEmailSuccess', 'Email was sent')
				}
				actionHasBeenConfirmed={sendEmailActionConfirmed}
				handleClose={handleConfirmFeedbackClose}
				error={sendEmailError}
			/>
		</div>
	);
};

const drawerWidth = 327;

const useStyles = makeStyles((theme) =>
	createStyles({
		actionPanel: {
			paddingBottom: theme.spacing(1),
			color: theme.palette.colors.pru.main,
		},
		discountBadge: {
			height: 10,
			minWidth: 10,
			backgroundColor: theme.palette.success.main,
		},
		dotBadgeStyles: {
			top: '7%',
			right: '7%',
		},
		container: {
			display: 'flex',
		},
		drawer: {
			[theme.breakpoints.up('md')]: {
				width: drawerWidth,
				flexShrink: 0,
			},
		},
		drawerPaper: {
			width: drawerWidth,
			zIndex: 1,
			overflow: 'visible',
			[theme.breakpoints.up('md')]: {
				paddingTop: 96,
			},
		},
		actionBtnWrapper: {
			flex: 0,
			padding: theme.spacing(1, 2),
			paddingTop: 0,
		},
		contentWrapper: {
			flex: 1,
			overflow: 'auto',
			padding: theme.spacing(2),
			paddingBottom: theme.spacing(1),
		},
		sidebarContentContainer: {
			flex: 1,
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			overflow: '-moz-scrollbars-none',
			overflowY: 'auto',
			'-ms-overflow-style': 'none',
			'&::-webkit-scrollbar': {
				width: 0,
			},
			'-webkit-overflow-scrolling': 'auto',
		},
		responsibleShopperContainer: {
			paddingBottom: 8,
		},
		iconButton: {
			width: theme.spacing(4),
			height: theme.spacing(4),
			marginLeft: theme.spacing(1),
			padding: 0,
		},
		editButtonContainer: {
			display: 'inline-flex',
		},
		divider: {
			margin: theme.spacing(1, 0),
		},
		bookingInfo: {
			marginBottom: theme.spacing(1),
		},
		channelGrid: {
			display: 'flex',
			flexDirection: 'column',
		},
		right: {
			textAlign: 'right',
		},
	}),
);

export default SideBar;
